import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Shield,
  Calendar,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  FileText,
  Inbox,
  Send,
  Sparkles,
  Building2,
  Mail,
  ExternalLink
} from 'lucide-react';

const ConformitePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-emerald-50/30">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg sm:text-xl font-['Manrope']" data-testid="conformite-home-link">
            <span className="text-emerald-600">Capsule</span> Patrimoine
          </Link>
          <Link to="/preparer">
            <Button variant="outline" size="sm" data-testid="conformite-cta-demo">
              Tester l'outil
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-12">
        {/* Hero */}
        <section className="text-center space-y-5">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-1.5 rounded-full text-sm font-medium" data-testid="conformite-badge">
            <Shield className="h-4 w-4" />
            Information officielle — Réforme DGFiP
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold font-['Manrope'] leading-tight">
            Facturation électronique <span className="text-emerald-600">obligatoire : 2026-2027</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Vous êtes auto-entrepreneur ? Voici ce qui change concrètement pour vous,
            les dates à retenir, et comment vous y préparer dès maintenant — sans paniquer.
          </p>
        </section>

        {/* 2 dates clés */}
        <section>
          <h2 className="text-2xl font-bold mb-6 font-['Manrope']">📅 Les 2 dates à retenir</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <Card className="border-l-4 border-l-blue-500" data-testid="date-1">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Calendar className="h-5 w-5" />
                  <span className="font-bold">1ER SEPTEMBRE 2026</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Recevoir des factures électroniques</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Depuis cette date, <strong>toutes les entreprises, y compris les micro-entrepreneurs</strong>,
                  doivent être en capacité de <strong>recevoir</strong> des factures électroniques
                  via une Plateforme Agréée (PA), même en franchise de TVA.
                </p>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    <Inbox className="h-3 w-3 inline mr-1" />
                    <strong>Concrètement :</strong> vous devez avoir une "boîte aux lettres" PA pour recevoir vos factures fournisseurs.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500" data-testid="date-2">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-emerald-700 mb-2">
                  <Calendar className="h-5 w-5" />
                  <span className="font-bold">1ER SEPTEMBRE 2027</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Émettre vos factures électroniques</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Les <strong>micro-entreprises</strong> devront <strong>émettre</strong> leurs factures
                  électroniques pour leurs clients professionnels (B2B) via une PA, et réaliser
                  le <strong>e-reporting</strong> lorsqu&apos;elles sont concernées.
                </p>
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                  <p className="text-xs text-emerald-900">
                    <Send className="h-3 w-3 inline mr-1" />
                    <strong>Concrètement :</strong> finis les PDF par email — vos factures B2B passeront par une PA au format Factur-X.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Sanctions — renvoi aux textes officiels, sans montant affirmé */}
        <section>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-3 text-amber-900">⚠️ En cas de manquement</h3>
                  <p className="text-sm text-amber-900">
                    Le Code général des impôts prévoit des sanctions en cas de non-respect des obligations de
                    facturation électronique et d&apos;e-reporting. Les montants et modalités applicables sont
                    fixés par les textes en vigueur : référez-vous aux sources officielles ci-dessous
                    (impots.gouv.fr, economie.gouv.fr) pour la version à jour.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Comment Capsule Patrimoine vous prépare */}
        <section>
          <h2 className="text-2xl font-bold mb-6 font-['Manrope']">
            🛡️ Comment <span className="text-emerald-600">Capsule Patrimoine</span> vous prépare
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                icon: FileText,
                title: 'Factures préparées au format Factur-X',
                desc: 'Champ SIREN client intégré sur toutes vos factures dès aujourd\'hui — prêt pour 2027.',
                status: 'Actif'
              },
              {
                icon: Inbox,
                title: 'Réception de factures fournisseurs',
                desc: 'Module dédié pour centraliser et archiver les factures reçues via votre Plateforme Agréée.',
                status: 'En préparation'
              },
              {
                icon: Send,
                title: 'Émission via PA partenaire',
                desc: 'Un clic pour transmettre vos factures B2B au format électronique conforme.',
                status: 'En préparation'
              },
              {
                icon: Shield,
                title: 'Veille réglementaire continue',
                desc: 'Mises à jour automatiques de l\'app à chaque évolution du décret d\'application.',
                status: 'Actif'
              }
            ].map((item) => (
              <Card key={item.title} className="hover:shadow-md transition-shadow" data-testid={`feature-${item.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-semibold text-sm">{item.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          item.status === 'Actif'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl font-bold mb-6 font-['Manrope']">❓ Questions fréquentes</h2>
          <div className="space-y-3">
            {[
              {
                q: 'Je suis en franchise de TVA, suis-je concerné(e) ?',
                a: 'Oui. Toutes les entreprises, y compris les micro-entrepreneurs en franchise de TVA, sont concernées. Vous devez être en capacité de RECEVOIR des factures électroniques depuis le 1er septembre 2026, et vous devrez en ÉMETTRE pour vos clients professionnels (avec e-reporting si vous êtes concerné(e)) à partir du 1er septembre 2027.'
              },
              {
                q: 'Qu\'est-ce qu\'une Plateforme Agréée (PA / PDP) ?',
                a: 'C\'est un intermédiaire technique certifié par la DGFiP qui se charge de transmettre, recevoir et stocker les factures électroniques. C\'est l\'équivalent d\'un facteur numérique entre vous, vos clients et l\'administration fiscale.'
              },
              {
                q: 'Dois-je payer un abonnement à une PA ?',
                a: 'Oui, généralement entre 5 et 30 €/mois selon le volume. Capsule Patrimoine intégrera une PA partenaire au tarif optimisé pour les auto-entrepreneurs au moment où l\'obligation prendra effet.'
              },
              {
                q: 'Et pour mes clients particuliers (B2C) ?',
                a: 'L\'obligation de facturation électronique concerne uniquement le B2B (clients professionnels). Pour vos clients particuliers, vous continuerez d\'émettre des factures classiques (PDF, papier), mais vous devrez déclarer le total à la DGFiP via e-reporting.'
              },
              {
                q: 'Quand dois-je agir ?',
                a: 'Dès maintenant : l\'obligation de réception est déjà en vigueur depuis le 1er septembre 2026, vous devez donc disposer d\'une Plateforme Agréée pour recevoir vos factures fournisseurs. Pour l\'émission (1er septembre 2027), commencez par récupérer les SIREN de vos clients professionnels — vous pouvez les ajouter dans vos factures Capsule Patrimoine dès aujourd\'hui.'
              }
            ].map((item, i) => (
              <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer p-4 font-semibold text-sm hover:bg-slate-50 transition-colors flex items-center justify-between">
                  {item.q}
                  <span className="text-emerald-600 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Sources officielles */}
        <section>
          <Card className="bg-slate-50/80">
            <CardContent className="p-5 text-xs text-muted-foreground">
              <p className="font-semibold text-slate-700 mb-2">📚 Sources officielles</p>
              <ul className="space-y-1">
                <li>• <a href="https://www.impots.gouv.fr/portail/professionnel/facturation-electronique" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">impots.gouv.fr — Facturation électronique <ExternalLink className="h-3 w-3" /></a></li>
                <li>• <a href="https://www.economie.gouv.fr/cedef/facturation-electronique-obligatoire-entreprises" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">economie.gouv.fr — Calendrier officiel <ExternalLink className="h-3 w-3" /></a></li>
                <li>• Articles 289 bis et 289 ter du Code Général des Impôts (CGI)</li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-2xl p-8 sm:p-12">
          <Sparkles className="h-10 w-10 mx-auto mb-4 text-emerald-100" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-['Manrope']">
            Préparez-vous sereinement à la réforme
          </h2>
          <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
            Capsule Patrimoine intègre dès aujourd'hui les fonctionnalités préparatoires
            et vous accompagnera pas à pas jusqu'à l'échéance.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/preparer">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-slate-100 font-bold" data-testid="conformite-cta-test">
                Tester gratuitement <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:contact@capsulepatrimoine.com?subject=Question%20sur%20la%20réforme%20facturation%20électronique%202026">
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10" data-testid="conformite-cta-contact">
                <Mail className="h-4 w-4 mr-2" /> Nous écrire
              </Button>
            </a>
          </div>
        </section>

        {/* Signature */}
        <div className="text-center text-xs text-muted-foreground border-t border-slate-200 pt-6">
          <p className="flex items-center justify-center gap-1">
            <Building2 className="h-3 w-3" />
            Capsule Patrimoine — Valérie Girin · SIRET 484 280 771 00060
          </p>
          <p className="mt-1">
            Cette page est informative et ne constitue pas un conseil juridique ou fiscal personnalisé.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ConformitePage;
